import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BlockMath, InlineMath } from 'react-katex';
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SectionWrapper from '@/components/SectionWrapper';
import FormulaCard from '@/components/FormulaCard';
import PhasorVisualizer from '@/calculators/PhasorVisualizer';
import WyeDeltaConverter from '@/calculators/WyeDeltaConverter';
import ThreePhasePowerCalc from '@/calculators/ThreePhasePowerCalc';

// ─── Section 1: Why It Matters ────────────────────────────────────────────────
const WhyItMatters = () => (
  <SectionWrapper number="1" title="Why It Matters" id="why-it-matters">
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-5 mb-5">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">Real Utility Scenario</p>
      <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm">
        A substation transformer is rated 30 MVA at 138/13.8 kV, 0.9 power factor. The protection engineer
        must set the overcurrent relay pickup. To do this, they need the full-load line current:{' '}
        <InlineMath math="I_L = \dfrac{S_{3\phi}}{\sqrt{3}\cdot V_{LL}} = \dfrac{30{,}000{,}000}{1.732 \times 13{,}800} = 1{,}255\,\text{A}" />.
        This single formula is the result of understanding three-phase power, phasors, and the √3 relationship
        between line and phase quantities — all topics in this module.
      </p>
    </div>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
      Every power system — from a 765 kV transmission backbone to a 120 V household outlet — operates on
      alternating current. Three-phase AC is the standard because it delivers <strong className="text-slate-800 dark:text-slate-200">constant instantaneous power</strong> to balanced
      loads (unlike single-phase, which pulsates at twice the frequency), uses conductors{' '}
      <strong className="text-slate-800 dark:text-slate-200">13% more efficiently</strong> than equivalent single-phase systems, and naturally produces a rotating
      magnetic field that drives motors.
    </p>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
      Analyzing a three-phase network requires the mathematical language of <strong className="text-slate-800 dark:text-slate-200">phasors</strong> — complex
      numbers that represent sinusoidal quantities by their RMS magnitude and phase angle. Without phasors, you
      cannot calculate fault currents, design relay coordination, verify voltage limits, or compute the reactive
      power your capacitor bank must compensate.
    </p>
  </SectionWrapper>
);

// ─── Section 2: Concept Visual ────────────────────────────────────────────────
const ConceptVisual = () => (
  <SectionWrapper number="2" title="Concept Visual" id="concept-visual" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      The diagram below shows a balanced wye (Y) source on the left, the three phase phasors in the middle,
      and the construction of V<sub>LL</sub> (line-to-line voltage) as a phasor difference on the right.
    </p>
    <div className="overflow-x-auto">
      <svg viewBox="0 0 720 240" className="w-full max-w-3xl mx-auto" style={{ minWidth: 340 }}>

        {/* ── LEFT: Wye source circuit ── */}
        <text x="70" y="18" fontSize="11" fill="#94a3b8" fontFamily="IBM Plex Mono" fontWeight="600" textAnchor="middle">WYE (Y) SOURCE</text>
        {/* Neutral point N */}
        <circle cx="70" cy="125" r="5" fill="#64748b" />
        <text x="78" y="130" fontSize="10" fill="#64748b" fontFamily="IBM Plex Sans">N</text>
        {/* Phase A (teal) — upper right */}
        <line x1="70" y1="125" x2="125" y2="65" stroke="#0d9488" strokeWidth="2" />
        <circle cx="125" cy="65" r="6" fill="none" stroke="#0d9488" strokeWidth="2" />
        <text x="133" y="70" fontSize="11" fill="#0d9488" fontFamily="IBM Plex Sans" fontWeight="700">A</text>
        <text x="86" y="85" fontSize="9" fill="#0d9488" fontFamily="IBM Plex Mono">Va</text>
        {/* Phase B (indigo) — lower right */}
        <line x1="70" y1="125" x2="125" y2="155" stroke="#6366f1" strokeWidth="2" />
        <circle cx="125" cy="155" r="6" fill="none" stroke="#6366f1" strokeWidth="2" />
        <text x="133" y="160" fontSize="11" fill="#6366f1" fontFamily="IBM Plex Sans" fontWeight="700">B</text>
        <text x="86" y="148" fontSize="9" fill="#6366f1" fontFamily="IBM Plex Mono">Vb</text>
        {/* Phase C (amber) — straight right */}
        <line x1="70" y1="125" x2="30" y2="160" stroke="#f59e0b" strokeWidth="2" />
        <circle cx="30" cy="160" r="6" fill="none" stroke="#f59e0b" strokeWidth="2" />
        <text x="6" y="165" fontSize="11" fill="#f59e0b" fontFamily="IBM Plex Sans" fontWeight="700">C</text>
        <text x="34" y="150" fontSize="9" fill="#f59e0b" fontFamily="IBM Plex Mono">Vc</text>
        {/* Ground symbol */}
        <line x1="70" y1="130" x2="70" y2="185" stroke="#64748b" strokeWidth="1.5" />
        <line x1="55" y1="185" x2="85" y2="185" stroke="#64748b" strokeWidth="2" />
        <line x1="60" y1="191" x2="80" y2="191" stroke="#64748b" strokeWidth="1.5" />
        <line x1="65" y1="197" x2="75" y2="197" stroke="#64748b" strokeWidth="1" />

        {/* ── MIDDLE: Phasor diagram ── */}
        <text x="290" y="18" fontSize="11" fill="#94a3b8" fontFamily="IBM Plex Mono" fontWeight="600" textAnchor="middle">PHASE PHASORS (V_LN)</text>
        {/* Axes */}
        <line x1="230" y1="125" x2="360" y2="125" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 3" />
        <line x1="290" y1="60" x2="290" y2="190" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 3" />
        {/* Circle */}
        <circle cx="290" cy="125" r="58" fill="none" stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />
        {/* Va phasor: 0° → right */}
        <line x1="290" y1="125" x2="342" y2="125" stroke="#0d9488" strokeWidth="2.5" />
        <polygon points="348,125 338,120 338,130" fill="#0d9488" />
        <text x="352" y="122" fontSize="11" fill="#0d9488" fontFamily="IBM Plex Mono" fontWeight="700">Va</text>
        <text x="352" y="134" fontSize="8.5" fill="#0d9488" fontFamily="IBM Plex Mono">∠0°</text>
        {/* Vb phasor: −120° → lower left */}
        <line x1="290" y1="125" x2="261" y2="175" stroke="#6366f1" strokeWidth="2.5" />
        <polygon points="255,184 262,172 270,178" fill="#6366f1" />
        <text x="240" y="195" fontSize="11" fill="#6366f1" fontFamily="IBM Plex Mono" fontWeight="700">Vb ∠−120°</text>
        {/* Vc phasor: +120° → upper left */}
        <line x1="290" y1="125" x2="261" y2="75" stroke="#f59e0b" strokeWidth="2.5" />
        <polygon points="255,66 262,78 270,72" fill="#f59e0b" />
        <text x="232" y="60" fontSize="11" fill="#f59e0b" fontFamily="IBM Plex Mono" fontWeight="700">Vc ∠+120°</text>

        {/* ── RIGHT: V_LL construction ── */}
        <text x="560" y="18" fontSize="11" fill="#94a3b8" fontFamily="IBM Plex Mono" fontWeight="600" textAnchor="middle">V_LL CONSTRUCTION</text>
        {/* Va tip */}
        <circle cx="560" cy="95" r="4" fill="#0d9488" />
        <text x="566" y="92" fontSize="10" fill="#0d9488" fontFamily="IBM Plex Mono">tip of Va</text>
        {/* Vb tip */}
        <circle cx="510" cy="175" r="4" fill="#6366f1" />
        <text x="516" y="190" fontSize="10" fill="#6366f1" fontFamily="IBM Plex Mono">tip of Vb</text>
        {/* Neutral / Origin */}
        <circle cx="500" cy="125" r="3.5" fill="#64748b" />
        <text x="486" y="122" fontSize="9" fill="#64748b" fontFamily="IBM Plex Mono">N</text>
        {/* Va */}
        <line x1="500" y1="125" x2="556" y2="97" stroke="#0d9488" strokeWidth="2" />
        {/* Vb */}
        <line x1="500" y1="125" x2="512" y2="171" stroke="#6366f1" strokeWidth="2" />
        {/* V_AB = Va − Vb */}
        <line x1="512" y1="171" x2="556" y2="97" stroke="#0B172A" strokeWidth="2.5" strokeDasharray="5 2" />
        <text x="550" y="152" fontSize="9" fill="#0B172A" fontFamily="IBM Plex Mono" fontWeight="700">V_AB</text>
        {/* Bracket showing √3 factor */}
        <line x1="630" y1="95" x2="630" y2="175" stroke="#94a3b8" strokeWidth="1" />
        <text x="635" y="140" fontSize="9.5" fill="#0d9488" fontFamily="IBM Plex Sans">= √3·V_LN</text>
        <text x="635" y="152" fontSize="9" fill="#94a3b8" fontFamily="IBM Plex Mono">∠ +30°</text>
      </svg>
    </div>
    <p className="text-xs text-slate-500 dark:text-slate-500 text-center mt-2">
      V<sub>AB</sub> = V<sub>a</sub> − V<sub>b</sub> is a phasor difference. Its magnitude is √3 × V<sub>LN</sub> and it leads V<sub>a</sub> by 30°.
    </p>
  </SectionWrapper>
);

// ─── Section 3: Core Theory ───────────────────────────────────────────────────
const CoreTheory = () => (
  <SectionWrapper number="3" title="Core Theory" id="core-theory">
    <div className="space-y-7">

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">A. AC Phasors and Complex Notation</h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm mb-3">
          A sinusoidal voltage <InlineMath math="v(t) = V_m\cos(\omega t + \theta)" /> is represented as a phasor{' '}
          <InlineMath math="\mathbf{V} = V_{rms}\angle\theta" />, where{' '}
          <InlineMath math="V_{rms} = V_m / \sqrt{2}" />. Power systems always use RMS magnitudes —
          equipment ratings, relay settings, and metering are all in RMS values.
        </p>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
          Phasors are complex numbers: <InlineMath math="\mathbf{V} = V_{rms}\cos\theta + j\,V_{rms}\sin\theta" />.
          Multiplication in polar form is straightforward: magnitudes multiply, angles add.
          This is why phasors simplify AC circuit analysis — differentiation and integration become simple
          multiplication by <InlineMath math="j\omega L" /> or division by <InlineMath math="j\omega C" />.
        </p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">B. Phase Sequence and Symmetry</h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm mb-3">
          The standard <strong className="text-slate-800 dark:text-slate-200">positive (ABC) sequence</strong> means phase A leads B by 120°, and B leads C by 120°:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 px-3 text-left font-medium text-slate-500 dark:text-slate-500">Phasor</th>
                <th className="py-2 px-3 text-left font-medium text-slate-500 dark:text-slate-500">General form</th>
                <th className="py-2 px-3 text-left font-medium text-slate-500 dark:text-slate-500">Example: V = 7.967 kV</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {[
                ['Va', 'V∠0°', '7.967∠0° kV'],
                ['Vb', 'V∠−120°', '7.967∠−120° kV'],
                ['Vc', 'V∠+120°', '7.967∠+120° kV'],
                ['Va + Vb + Vc', '0 (balanced)', '0 + j0 (neutral = 0 V)'],
              ].map(([p, f, e]) => (
                <tr key={p} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="py-2 px-3 font-mono text-xs text-teal-700 dark:text-teal-400">{p}</td>
                  <td className="py-2 px-3 font-mono text-xs text-slate-700 dark:text-slate-300">{f}</td>
                  <td className="py-2 px-3 text-xs text-slate-500 dark:text-slate-500">{e}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">C. Wye and Delta Connections</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              title: 'Wye (Y)',
              bg: 'bg-teal-50 dark:bg-teal-900/10 border-teal-200 dark:border-teal-800',
              items: [
                'V_LL = √3 × V_LN (magnitude), leading V_LN by 30°',
                'I_line = I_phase (only one current path per arm)',
                'Neutral point N exists — zero-sequence current can flow',
                'Most generator and transformer HV windings are wye',
              ],
            },
            {
              title: 'Delta (Δ)',
              bg: 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800',
              items: [
                'V_phase = V_LL (winding is directly across lines)',
                'I_L = √3 × I_phase (magnitude), lagging I_phase by 30°',
                'No neutral — zero-sequence current cannot flow through a delta',
                'Blocks zero-sequence, used to isolate ground faults',
              ],
            },
          ].map(({ title, bg, items }) => (
            <div key={title} className={`border rounded-lg p-4 ${bg}`}>
              <h4 className="font-semibold text-slate-900 dark:text-white mb-3 font-heading">{title}</h4>
              <ul className="space-y-1.5">
                {items.map(item => (
                  <li key={item} className="text-xs text-slate-700 dark:text-slate-300 flex items-start gap-2">
                    <span className="text-teal-600 dark:text-teal-400 font-bold mt-0.5">→</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 font-heading">D. Balanced Per-Phase Equivalent Circuit</h3>
        <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">
          For a balanced three-phase system, all three phases are identical in magnitude and displaced by 120°.
          This symmetry means we can analyze <strong className="text-slate-800 dark:text-slate-200">one phase only</strong> and apply the result to all three.
          In the per-phase equivalent circuit: use <InlineMath math="V_{LN}" /> as the source,{' '}
          <InlineMath math="I_{line}" /> as the current, and divide the three-phase impedance by 3 (or use per-phase impedance directly).
          Total three-phase power = 3 × per-phase power = <InlineMath math="\sqrt{3}\,V_{LL}\,I_L\cos\phi" />.
        </p>
      </div>
    </div>
  </SectionWrapper>
);

// ─── Section 4: Formula Sheet ─────────────────────────────────────────────────
const FormulaSheet = () => (
  <SectionWrapper number="4" title="Formula Sheet" id="formulas" alt>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormulaCard
        title="Phasor Notation (IEEE Standard)"
        formula={"\\mathbf{V} = V_{rms}\\angle\\theta = V_{rms}\\cos\\theta + j\\,V_{rms}\\sin\\theta"}
        variables={[
          { sym: 'V_rms', desc: 'RMS voltage magnitude (V or kV) — always RMS in power systems' },
          { sym: 'θ', desc: 'Phase angle (degrees or radians), measured from positive real axis' },
          { sym: 'j', desc: 'Imaginary unit, j = √(−1). Note: power systems use j, not i.' },
        ]}
        note="Assumption: sinusoidal steady-state. V_rms = V_peak / √2 ≈ 0.7071 × V_peak."
      />
      <FormulaCard
        title="Wye Voltage — Magnitude"
        formula={"V_{LL} = \\sqrt{3}\\cdot V_{LN}\\quad(\\text{magnitudes only})"}
        variables={[
          { sym: 'V_LL', desc: 'Line-to-line (phase-to-phase) RMS voltage (V or kV)' },
          { sym: 'V_LN', desc: 'Line-to-neutral (phase) RMS voltage (V or kV)' },
          { sym: '√3', desc: '≈ 1.7321 — arises from phasor subtraction Va − Vb' },
        ]}
        note="Example: 13.8 kV ÷ √3 = 7.967 kV. Always L-L on equipment nameplates."
      />
      <FormulaCard
        title="Wye Voltage — Full Phasor Form"
        formula={"\\mathbf{V}_{AB} = \\sqrt{3}\\,V_{LN}\\angle(\\theta_A + 30^\\circ)"}
        variables={[
          { sym: 'V_AB', desc: 'Line-to-line phasor from bus A to bus B' },
          { sym: 'θ_A', desc: 'Phase angle of Va (usually 0° as reference)' },
          { sym: '+30°', desc: 'V_AB leads Va by 30° in positive (ABC) sequence' },
        ]}
        note="Both forms required: use magnitude form for power calculations; full phasor form for relay analysis."
      />
      <FormulaCard
        title="Delta Current Relationship"
        formula={"I_L = \\sqrt{3}\\cdot I_{phase}\\angle(\\theta_{phase} - 30^\\circ)"}
        variables={[
          { sym: 'I_L', desc: 'Line current (current in feeder conductor, A)' },
          { sym: 'I_phase', desc: 'Delta branch current (through each Δ winding, A)' },
          { sym: '−30°', desc: 'I_L lags I_phase by 30° in positive (ABC) sequence' },
        ]}
        note="CTs always measure I_L. To find I_phase from metered line current: I_phase = I_L / √3."
      />
      <FormulaCard
        title="Three-Phase Apparent Power"
        formula={"S_{3\\phi} = \\sqrt{3}\\cdot V_{LL}\\cdot I_L"}
        variables={[
          { sym: 'S_3φ', desc: 'Three-phase apparent power (VA, kVA, or MVA)' },
          { sym: 'V_LL', desc: 'Line-to-line RMS voltage (V, kV)' },
          { sym: 'I_L', desc: 'Line current (A, kA)' },
        ]}
        note="Assumption: balanced three-phase system. Unit consistency: kV × A ÷ 1000 = MVA."
      />
      <FormulaCard
        title="Three-Phase Real, Reactive Power and Power Factor"
        formula={"P = \\sqrt{3}\\,V_{LL}\\,I_L\\cos\\phi \\qquad Q = \\sqrt{3}\\,V_{LL}\\,I_L\\sin\\phi"}
        variables={[
          { sym: 'P', desc: 'Real (active) power in watts (W, kW, or MW)' },
          { sym: 'Q', desc: 'Reactive power in vars (VAr, kVAr, or MVAr); positive = inductive (lagging)' },
          { sym: 'φ', desc: 'Power factor angle = angle between V and I phasors' },
          { sym: 'cos φ', desc: 'Power factor (pf) — dimensionless, 0 to 1.0' },
        ]}
        note="S² = P² + Q², so S = √(P² + Q²). Numerical example: P = 4.60 MW, Q = 1.96 MVAr → S = √(4.60² + 1.96²) = 5.0 MVA ✓"
      />
    </div>
  </SectionWrapper>
);

// ─── Section 5: Common Mistakes ───────────────────────────────────────────────
const MISTAKES = [
  {
    title: 'Using peak voltage (V_m) instead of RMS in power formulas',
    body: "Power system formulas — S = √3·V_LL·I_L, relay settings, impedance calculations — all assume RMS values. Equipment nameplates, meters, and instrument transformers all output RMS. Using V_m = √2 × V_rms in a power formula gives an answer that is √2 ≈ 1.414× too large. The 'V = 13.8 kV' on a distribution transformer nameplate is 13.8 kV RMS, not peak.",
  },
  {
    title: 'Using V_LN where V_LL is required in the three-phase power formula',
    body: "S_3φ = √3·V_LL·I_L uses the line-to-line voltage. Engineers occasionally substitute V_LN (= V_LL/√3), making the formula S = √3·(V_LL/√3)·I_L = V_LL·I_L — missing the √3 factor and understating apparent power by √3 ≈ 1.73×. Memorize: the √3 in the power formula compensates for using V_LL instead of V_LN.",
  },
  {
    title: "Applying the √3 factor to delta voltages (it only applies to delta currents)",
    body: "In a delta connection: V_phase = V_LL (no √3 for voltage). The √3 factor applies only to the delta current relationship: I_L = √3·I_phase. A common error is to write V_LN = V_LL/√3 for a delta, which has no physical meaning in delta since there is no neutral point. For delta loads, the full line-to-line voltage appears across each element.",
  },
  {
    title: "Forgetting the 30° phase shift between V_LN and V_LL",
    body: "The magnitude of V_LL = √3·V_LN is widely known, but V_AB also leads Va by 30° in positive sequence. For protection relaying (directional overcurrent, distance), the correct phase relationship is essential. A 30° error in the angular reference will cause incorrect reach and direction calculations for phase-to-phase relay elements.",
  },
  {
    title: 'Confusing per-phase power with three-phase power',
    body: "P_per-phase = V_LN·I_L·cos(φ) is the power in one phase of the per-phase equivalent circuit. Three-phase total is P_3φ = 3 × P_per-phase = √3·V_LL·I_L·cos(φ). Engineers who set up the per-phase circuit and then forget to multiply by 3 — or multiply by 3 and then also apply the √3·V_LL factor — are off by a factor of 3 or √3. Always state clearly whether you're computing per-phase or three-phase total.",
  },
];

const CommonMistakes = () => (
  <SectionWrapper number="5" title="Common Mistakes" id="common-mistakes">
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      These five errors appear repeatedly in PE exam grading reports and in distribution engineering reviews.
    </p>
    <Accordion type="single" collapsible className="space-y-2" data-testid="mistakes-accordion">
      {MISTAKES.map((m, i) => (
        <AccordionItem
          key={i}
          value={`mistake-${i}`}
          className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 bg-white dark:bg-slate-900"
        >
          <AccordionTrigger className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:no-underline hover:text-teal-700 dark:hover:text-teal-400 py-3">
            <span className="flex items-center gap-2.5">
              <span className="text-red-500 text-xs font-bold w-4 flex-shrink-0">#{i + 1}</span>
              {m.title}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pb-3">{m.body}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </SectionWrapper>
);

// ─── Section 6: Worked Example ────────────────────────────────────────────────
const STEPS = [
  {
    label: 'State the given quantities',
    text: 'System: 13.8 kV three-phase feeder. Load: S = 5 MVA at pf = 0.92 lagging. Required: line current I_L, real power P, reactive power Q, line-to-neutral voltage V_LN, and per-phase phasor diagram.',
    formula: null,
    result: null,
  },
  {
    label: 'Find the line current from apparent power',
    text: 'Rearrange S = √3·V_LL·I_L for I_L. Convert S to VA and V_LL to V for unit consistency.',
    formula: "I_L = \\dfrac{S_{3\\phi}}{\\sqrt{3}\\cdot V_{LL}} = \\dfrac{5{,}000{,}000}{1.7321 \\times 13{,}800} = \\dfrac{5{,}000{,}000}{23{,}902} = 209.2\\,\\text{A}",
    result: 'I_L = 209.2 A',
  },
  {
    label: 'Find real power P',
    text: 'Power factor pf = cos(φ) = 0.92. Real power P = S × pf.',
    formula: "P_{3\\phi} = S_{3\\phi} \\cdot \\text{pf} = 5\\,\\text{MVA} \\times 0.92 = 4.60\\,\\text{MW}",
    result: 'P = 4.60 MW',
  },
  {
    label: 'Find the power factor angle φ and reactive power Q',
    text: 'The power factor angle φ = arccos(0.92). Reactive power Q = S × sin(φ). Lagging pf means inductive load → Q is positive.',
    formula: "\\phi = \\arccos(0.92) = 23.07^\\circ \\qquad Q = 5 \\times \\sin(23.07^\\circ) = 5 \\times 0.3919 = 1.959\\,\\text{MVAr}",
    result: 'Q = 1.959 MVAr (lagging)',
  },
  {
    label: 'Find the line-to-neutral voltage for per-phase analysis',
    text: 'In a wye system, V_LN = V_LL / √3. This is the phase voltage used in the per-phase equivalent circuit.',
    formula: "V_{LN} = \\dfrac{V_{LL}}{\\sqrt{3}} = \\dfrac{13{,}800}{1.7321} = 7{,}967\\,\\text{V} = 7.967\\,\\text{kV}",
    result: 'V_LN = 7.967 kV',
  },
  {
    label: 'Write the per-phase phasors (Va as reference)',
    text: 'Taking Va as the reference phasor (angle = 0°), the load current lags Va by the power factor angle φ = 23.07°.',
    formula: "\\mathbf{V}_a = 7{,}967\\angle0^\\circ\\,\\text{V} \\qquad \\mathbf{I}_a = 209.2\\angle{-23.07^\\circ}\\,\\text{A}",
    result: 'Va = 7.967∠0° kV, Ia = 209.2∠−23.07° A',
  },
  {
    label: 'Sanity check with per-phase power',
    text: 'Per-phase real power P_1φ = V_LN × I_L × cos(φ). Three-phase total = 3 × P_1φ must equal the 4.60 MW we calculated.',
    formula: "P_{1\\phi} = 7{,}967 \\times 209.2 \\times 0.92 = 1{,}533{,}000\\,\\text{W} = 1.533\\,\\text{MW} \\quad\\Rightarrow\\quad P_{3\\phi} = 3 \\times 1.533 = 4.60\\,\\text{MW}\\;\\checkmark",
    result: 'Sanity check passes: 4.60 MW confirmed.',
  },
];

const WorkedExample = () => (
  <SectionWrapper number="6" title="Step-by-Step Worked Example" id="worked-example">
    <div className="mb-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest mb-3">Problem Statement</p>
      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
        A <strong>13.8 kV</strong> three-phase feeder delivers <strong>5 MVA</strong> at{' '}
        <strong>0.92 power factor lagging</strong>. The system is balanced and wye-connected.
        Find: (1) line current I<sub>L</sub>, (2) real power P, (3) reactive power Q,
        (4) line-to-neutral voltage V<sub>LN</sub>, and (5) the per-phase phasor diagram with V<sub>a</sub> as reference.
      </p>
    </div>
    <div className="space-y-3" data-testid="worked-example-steps">
      {STEPS.map((step, i) => (
        <div key={i} className="step-card border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900">
          <div className="flex items-start gap-3">
            <div className="section-badge flex-shrink-0 text-xs">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white text-sm mb-2 font-heading">{step.label}</p>
              {step.text && (
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-2">{step.text}</p>
              )}
              {step.formula && (
                <div className="overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3 my-2">
                  <BlockMath math={step.formula} />
                </div>
              )}
              {step.result && (
                <div className="inline-flex items-center gap-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-md px-3 py-1.5 mt-1">
                  <ChevronRight className="h-3 w-3 text-teal-600" />
                  <span className="text-xs font-mono font-semibold text-teal-800 dark:text-teal-300">{step.result}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </SectionWrapper>
);

// ─── Section 7: Calculators ───────────────────────────────────────────────────
const CalculatorsSection = () => (
  <SectionWrapper number="7" title="Interactive Calculators" id="calculators" alt>
    <Tabs defaultValue="phasor" className="w-full" data-testid="calculators-tabs">
      <TabsList className="mb-6 bg-slate-100 dark:bg-slate-800">
        <TabsTrigger value="phasor" data-testid="tab-phasor">Phasor Visualizer</TabsTrigger>
        <TabsTrigger value="wyedelta" data-testid="tab-wyedelta">Wye / Delta</TabsTrigger>
        <TabsTrigger value="power" data-testid="tab-power">Power Calculator</TabsTrigger>
      </TabsList>

      <TabsContent value="phasor">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Phasor Diagram Visualizer</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Set the RMS magnitude and the reference angle of V<sub>a</sub>. The diagram shows all three balanced ABC phasors
            in the complex plane, with polar and rectangular values in the table.
          </p>
        </div>
        <PhasorVisualizer />
      </TabsContent>

      <TabsContent value="wyedelta">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Wye / Delta Voltage-Current Converter</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Select the connection type, enter your known quantities, and instantly compute the related
            voltage and current values with a side-by-side comparison chart.
          </p>
        </div>
        <WyeDeltaConverter />
      </TabsContent>

      <TabsContent value="power">
        <div className="mb-4">
          <h3 className="font-semibold text-slate-900 dark:text-white font-heading mb-1">Three-Phase Power Calculator</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Enter V<sub>LL</sub>, I<sub>L</sub>, and power factor to compute S (MVA), P (MW), Q (MVAr), and
            the power factor angle φ. The power triangle and bar chart update in real time.
          </p>
        </div>
        <ThreePhasePowerCalc />
      </TabsContent>
    </Tabs>
  </SectionWrapper>
);

// ─── Section 8: Self-Check Quiz ───────────────────────────────────────────────
const QUESTIONS = [
  {
    q: 'A balanced wye source has V_LN = 7.967 kV. What is V_LL?',
    options: [
      { k: 'a', label: '7.967 kV' },
      { k: 'b', label: '11.27 kV' },
      { k: 'c', label: '13.8 kV' },
      { k: 'd', label: '23.9 kV' },
    ],
    correct: 'c',
    explanation: 'V_LL = √3 × V_LN = 1.7321 × 7.967 = 13.80 kV. This is the standard 13.8 kV distribution voltage — always specified as a line-to-line value.',
  },
  {
    q: 'A feeder carries I_L = 100 A and delivers S = 2.4 MVA. What is V_LL?',
    options: [
      { k: 'a', label: '2.4 kV' },
      { k: 'b', label: '13.86 kV' },
      { k: 'c', label: '24.0 kV' },
      { k: 'd', label: '138 kV' },
    ],
    correct: 'b',
    explanation: 'Rearrange S = √3·V_LL·I_L: V_LL = S / (√3·I_L) = 2,400,000 / (1.7321 × 100) = 13,856 V ≈ 13.86 kV. This is a 13.8 kV class feeder.',
  },
  {
    q: 'In a balanced ABC-sequence wye system, V_AB leads V_AN by how many degrees?',
    options: [
      { k: 'a', label: '0° (they are in phase)' },
      { k: 'b', label: 'V_AB lags V_AN by 30°' },
      { k: 'c', label: 'V_AB leads V_AN by 30°' },
      { k: 'd', label: '60°' },
    ],
    correct: 'c',
    explanation: 'V_AB = Va − Vb = √3·V_LN∠(θ_A + 30°). The line-to-line voltage V_AB leads the phase voltage Va (= V_AN) by 30°. This 30° shift matters in phase-to-phase directional relay analysis.',
  },
  {
    q: 'A delta load draws I_phase = 50 A through each branch. What is the line current I_L?',
    options: [
      { k: 'a', label: '28.9 A' },
      { k: 'b', label: '50.0 A' },
      { k: 'c', label: '86.6 A' },
      { k: 'd', label: '150.0 A' },
    ],
    correct: 'c',
    explanation: 'I_L = √3 × I_phase = 1.7321 × 50 = 86.6 A. This is the inverse of the wye voltage rule: for delta, the √3 applies to current. A CT on the line conductor will read 86.6 A, not 50 A.',
  },
  {
    q: 'A feeder delivers P = 3.0 MW at 0.85 power factor lagging. What is the reactive power Q?',
    options: [
      { k: 'a', label: '2.55 MVAr' },
      { k: 'b', label: '1.86 MVAr' },
      { k: 'c', label: '3.53 MVAr' },
      { k: 'd', label: '1.00 MVAr' },
    ],
    correct: 'b',
    explanation: 'First: S = P / pf = 3.0 / 0.85 = 3.529 MVA. Then: Q = S × sin(arccos(0.85)) = 3.529 × sin(31.79°) = 3.529 × 0.5268 = 1.859 ≈ 1.86 MVAr. Check: S² = P² + Q² → 3.529² = 3.0² + 1.86² = 9.0 + 3.46 = 12.46 ≈ 3.53² ✓',
  },
];

const SelfCheckQuiz = () => {
  const [selected, setSelected] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = submitted ? QUESTIONS.filter((q, i) => selected[i] === q.correct).length : 0;
  const reset = () => { setSelected({}); setSubmitted(false); };

  return (
    <SectionWrapper number="8" title="Self-Check Quiz" id="quiz">
      <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
        Five questions covering phasors, wye/delta relationships, and three-phase power. Select one answer per question, then click Submit.
      </p>
      <div className="space-y-6" data-testid="quiz-section">
        {QUESTIONS.map((q, qi) => (
          <div key={qi} data-testid={`quiz-question-${qi}`} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
            <p className="font-medium text-slate-900 dark:text-white text-sm mb-3">
              <span className="text-teal-700 dark:text-teal-400 font-mono mr-2">Q{qi + 1}.</span>
              {q.q}
            </p>
            <div className="space-y-2">
              {q.options.map(opt => {
                const isSel = selected[qi] === opt.k;
                const isCorrect = opt.k === q.correct;
                let cls = 'border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2 ';
                if (!submitted) {
                  cls += isSel
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-100'
                    : 'border-slate-200 dark:border-slate-700 hover:border-teal-400 text-slate-700 dark:text-slate-300';
                } else {
                  if (isCorrect) cls += 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100';
                  else if (isSel) cls += 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100';
                  else cls += 'border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-600 opacity-50';
                }
                return (
                  <button
                    key={opt.k}
                    data-testid={`quiz-option-${qi}-${opt.k}`}
                    className={cls}
                    onClick={() => !submitted && setSelected(prev => ({ ...prev, [qi]: opt.k }))}
                    disabled={submitted}
                  >
                    {submitted && isCorrect && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                    {submitted && isSel && !isCorrect && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                    <span className="font-mono text-xs font-bold mr-1">{opt.k.toUpperCase()}.</span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {submitted && (
              <div className="mt-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3">
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  <span className="font-semibold text-teal-700 dark:text-teal-400">Explanation: </span>
                  {q.explanation}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-6">
        {!submitted ? (
          <button
            data-testid="quiz-submit"
            onClick={() => Object.keys(selected).length > 0 && setSubmitted(true)}
            disabled={Object.keys(selected).length === 0}
            className="px-5 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors text-sm"
          >
            Submit Answers ({Object.keys(selected).length}/{QUESTIONS.length} answered)
          </button>
        ) : (
          <>
            <div
              data-testid="quiz-score"
              className={`px-4 py-2 rounded-md text-sm font-semibold ${
                score === QUESTIONS.length ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                : score >= 3 ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}
            >
              Score: {score}/{QUESTIONS.length}
              {score === QUESTIONS.length && ' — Perfect!'}
              {score >= 3 && score < QUESTIONS.length && ' — Good work'}
              {score < 3 && ' — Review the formulas and try again'}
            </div>
            <button data-testid="quiz-reset" onClick={reset} className="text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 underline transition-colors">
              Try again
            </button>
          </>
        )}
      </div>
    </SectionWrapper>
  );
};

// ─── Section 9: Connected Topics ──────────────────────────────────────────────
const ConnectedTopics = () => (
  <SectionWrapper number="9" title="Connected Topics" id="connected-topics" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      Foundation is the entry point — no prerequisites. Every module in this platform builds on these concepts.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">Prerequisites</p>
        <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg">
          <span className="text-xl">🎯</span>
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">No Prerequisites</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">This is the entry-level module. Start here.</p>
          </div>
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">Next Steps</p>
        <div className="space-y-2">
          {[
            { to: '/transformers', label: 'M02 Transformer Systems', desc: 'Turns ratio, leakage reactance, delta-wye' },
            { to: '/per-unit', label: 'M03 Per-Unit Analysis', desc: 'Uses V_LL, I_base, and √3 formulas from here' },
          ].map(({ to, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-3 border border-teal-100 dark:border-teal-900/40 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-teal-50/50 dark:bg-teal-900/10"
            >
              <ArrowRight className="h-4 w-4 text-teal-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
    <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
      <p className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2">Foundation formulas used in every subsequent module:</p>
      <div className="flex flex-wrap gap-2">
        {['S = √3·V_LL·I_L', 'V_LL = √3·V_LN', 'I_L = √3·I_phase (Δ)', 'pf = cos(φ)', 'I_base = S/(√3·V)', 'V_pu = V/V_base'].map(f => (
          <span key={f} className="text-xs font-mono bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-teal-700 dark:text-teal-400 px-2 py-1 rounded">
            {f}
          </span>
        ))}
      </div>
    </div>
  </SectionWrapper>
);

// ─── Main Module Page ──────────────────────────────────────────────────────────
const FoundationModule = () => (
  <div data-testid="foundation-module">
    {/* Module Header */}
    <div className="relative bg-gradient-to-br from-[#0B172A] via-indigo-950 to-teal-900 text-white px-4 sm:px-6 lg:px-8 py-12 overflow-hidden">
      <div className="relative max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-5 transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Curriculum Map
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono font-semibold text-teal-400 bg-teal-400/10 border border-teal-400/20 px-2 py-1 rounded">M01</span>
          <span className="text-xs font-bold bg-teal-500 text-white px-2 py-0.5 rounded-full">LIVE</span>
          <span className="text-xs text-slate-400 uppercase tracking-widest">No Prerequisites</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold font-heading mb-3 leading-tight">Foundation</h1>
        <p className="text-slate-300 text-lg mb-4">Three-Phase Fundamentals — phasors, wye/delta, and power</p>
        <p className="text-slate-400 text-sm max-w-2xl">
          The mathematical and conceptual foundation of all power systems analysis. Master this module
          before proceeding to transformers, per-unit analysis, or fault calculations.
        </p>
      </div>
    </div>

    {/* Section Nav */}
    <div className="sticky top-14 z-20 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        <div className="flex gap-0 py-0" style={{ scrollbarWidth: 'none' }}>
          {[
            { n: 1, label: 'Why', id: 'why-it-matters' },
            { n: 2, label: 'Visual', id: 'concept-visual' },
            { n: 3, label: 'Theory', id: 'core-theory' },
            { n: 4, label: 'Formulas', id: 'formulas' },
            { n: 5, label: 'Mistakes', id: 'common-mistakes' },
            { n: 6, label: 'Example', id: 'worked-example' },
            { n: 7, label: 'Calculators', id: 'calculators' },
            { n: 8, label: 'Quiz', id: 'quiz' },
            { n: 9, label: 'Connections', id: 'connected-topics' },
          ].map(({ n, label, id }) => (
            <a
              key={n}
              href={`#${id}`}
              className="px-3 py-3 text-xs font-medium text-slate-500 dark:text-slate-500 hover:text-teal-700 dark:hover:text-teal-400 whitespace-nowrap border-b-2 border-transparent hover:border-teal-500 transition-colors flex-shrink-0"
            >
              <span className="text-teal-600 dark:text-teal-500 mr-1 font-mono">{n}.</span>{label}
            </a>
          ))}
        </div>
      </div>
    </div>

    {/* All 9 Sections */}
    <WhyItMatters />
    <ConceptVisual />
    <CoreTheory />
    <FormulaSheet />
    <CommonMistakes />
    <WorkedExample />
    <CalculatorsSection />
    <SelfCheckQuiz />
    <ConnectedTopics />

    {/* Bottom nav */}
    <div className="border-t border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-4xl mx-auto flex items-center justify-end">
        <Link
          to="/transformers"
          data-testid="next-module-btn"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
        >
          M02 Transformer Systems
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </div>
);

export default FoundationModule;
